import { z } from 'zod';
import { segmentRuleSchema } from '../../schemas/segmentDefinition.js';
import { CAMPAIGN_TYPES } from '../../schemas/campaign.js';
import { sequenceBranchRuleSchema } from '../../sequences/branchRules.js';
import { ACTION_VALUE_SOURCES } from './provenance.js';
const provenanceSchema = z.record(z.string(), z.enum(ACTION_VALUE_SOURCES)).optional();
export const createAudienceActionPayloadSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(5000).optional(),
    segmentRules: z.array(segmentRuleSchema).max(32),
    sourcePlan: z.object({
        audienceDescription: z.string().max(2000),
        locations: z.array(z.string()).optional(),
        industries: z.array(z.string()).optional(),
        companySize: z.object({ min: z.number(), max: z.number() }).optional(),
        jobRoles: z.array(z.string()).optional(),
        exclusions: z.array(z.string()).optional(),
    }),
    provenance: provenanceSchema,
});
export const createCampaignDraftActionPayloadSchema = z
    .object({
    name: z.string().min(1).max(200),
    objective: z.string().min(1).max(500),
    channel: z.enum(['email', 'voice']).default('email'),
    type: z.enum(CAMPAIGN_TYPES).default('cold_outreach'),
    audienceActionId: z.string().uuid().optional(),
    existingAudienceId: z.string().uuid().optional(),
    offer: z.string().max(2000).optional(),
    callToAction: z.string().max(500).optional(),
    provenance: provenanceSchema,
})
    .superRefine((d, ctx) => {
    if (d.audienceActionId && d.existingAudienceId) {
        ctx.addIssue({
            code: 'custom',
            message: 'audienceActionId and existingAudienceId are mutually exclusive',
            path: ['audienceActionId'],
        });
    }
});
export const proposedSequenceStepSchema = z
    .object({
    step_order: z.number().int().min(0),
    wait_days: z.number().int().min(0).max(365),
    subject_template: z.string().max(998).default(''),
    body_text_template: z.string().max(100_000).default(''),
    branch_rules: z.array(sequenceBranchRuleSchema).max(10).optional(),
    step_channel: z.enum(['email', 'voice']).optional(),
})
    .superRefine((step, ctx) => {
    if (step.step_channel === 'voice')
        return;
    if (!step.subject_template.trim()) {
        ctx.addIssue({ code: 'custom', message: 'subject required', path: ['subject_template'] });
    }
    if (!step.body_text_template.trim()) {
        ctx.addIssue({ code: 'custom', message: 'body required', path: ['body_text_template'] });
    }
});
export const createSequenceActionPayloadSchema = z.object({
    name: z.string().min(1).max(200),
    campaignActionId: z.string().uuid(),
    steps: z.array(proposedSequenceStepSchema).min(1).max(20),
    provenance: provenanceSchema,
});
export const templateDraftStepSchema = z.object({
    stepOrder: z.number().int().min(0),
    subject: z.string().max(998),
    bodyText: z.string().max(100_000),
    angle: z.string().max(500).optional(),
});
export const createTemplateDraftActionPayloadSchema = z.object({
    sequenceActionId: z.string().uuid(),
    templates: z.array(templateDraftStepSchema).min(1).max(20),
    provenance: provenanceSchema,
});
export const prepareEnrollmentActionPayloadSchema = z.object({
    campaignActionId: z.string().uuid(),
    audienceActionId: z.string().uuid(),
    maxLeads: z.number().int().min(1).max(100_000),
    acquisitionMethod: z.enum(['existing_leads', 'scrape', 'import', 'unknown']),
    policyNotes: z.string().max(2000).optional(),
    provenance: provenanceSchema,
});
const payloadByType = {
    CREATE_AUDIENCE: createAudienceActionPayloadSchema,
    CREATE_CAMPAIGN_DRAFT: createCampaignDraftActionPayloadSchema,
    CREATE_SEQUENCE: createSequenceActionPayloadSchema,
    CREATE_TEMPLATE_DRAFT: createTemplateDraftActionPayloadSchema,
    PREPARE_ENROLLMENT: prepareEnrollmentActionPayloadSchema,
};
export function validateActionPayload(type, payload) {
    if (type === 'START_SCRAPE') {
        return { ok: false, errors: ['action_type_deferred'] };
    }
    const schema = payloadByType[type];
    if (!schema)
        return { ok: false, errors: ['unknown_action_type'] };
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
        return { ok: false, errors: parsed.error.issues.map((i) => i.message) };
    }
    return { ok: true, payload: parsed.data };
}
//# sourceMappingURL=payloads.js.map